import React, { useState, useCallback } from 'react';
import { TabState, createDatasetId, createTransformOperationId, DatasetId, ColumnId } from '../../../../types';
import { DerivedDataSet } from '../../../../types/dataset.types';
import { useProjectData } from '../../../stores/hooks/useProjectData';
import { usePaneLayout } from '../../../stores/hooks/usePaneLayout';
import { useTabOperations } from '../../../hooks/useTabOperations';
import { useProjectStore } from '../../../stores/projectStore';
import { useNotificationStore } from '../../../stores/notificationStore';
import { createDataTableTab } from '../../../tab-system/TabRegistry';
import { createDummyCodedDataset } from '../../../utils/create-dummy-coded-dataset';
import { DummyCodingSettings, type ColumnActionMap, type ColumnScaleMap } from '../../DataTransform/DummyCodingSettings';
import { Dialog } from '../../common/Dialog';
import { Button } from '../../common/Button';
import logger from '../../../utils/logger';
import * as styles from './DummyCodingTabContent.css';

/**
 * DummyCodingTabContent
 *
 * カテゴリカル変数をダミー変数化するタブ
 */

interface DummyCodingTabContentProps {
  tab: TabState;
}

export const DummyCodingTabContent: React.FC<DummyCodingTabContentProps> = ({ tab }) => {
  const { datasets, addDerivedDataset } = useProjectData();
  const paneLayout = usePaneLayout();
  const updateTabTitle = useProjectStore((state) => state.updateTabTitle);
  const updateTabState = useProjectStore((state) => state.updateTabState);
  const findTab = useProjectStore((state) => state.findTab);
  const { error: showError } = useNotificationStore();
  const tabOperations = useTabOperations({
    project: null,
    layout: {
      rootPane: paneLayout.layout.rootPane,
      activeContainerId: paneLayout.layout.activeContainerId ?? null
    },
    addTab: paneLayout.addTab,
    addTabWithOptions: paneLayout.addTabWithOptions,
    closeTab: paneLayout.closeTab,
    moveTabWithinPane: paneLayout.moveTabWithinPane,
    moveTabToPane: paneLayout.moveTabToPane,
    startDrag: paneLayout.startDrag,
    endDrag: paneLayout.endDrag
  });

  // データセット選択状態
  const [selectedDatasetId, setSelectedDatasetId] = useState<DatasetId | undefined>(
    tab.metadata?.datasetId
  );

  const dataset = selectedDatasetId ? datasets[selectedDatasetId] : undefined;

  // 選択された列
  const [selectedColumns, setSelectedColumns] = useState<ColumnId[]>([]);
  // 各列のアクション（dummy_code / include_as_is / not_included）
  const [columnActions, setColumnActions] = useState<ColumnActionMap>({});
  // 各列のスケールオーバーライド
  const [columnScales, setColumnScales] = useState<ColumnScaleMap>({});
  const [isApplying, setIsApplying] = useState(false);

  // データセット名入力ダイアログ
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [newDatasetName, setNewDatasetName] = useState('');

  // データセット選択ハンドラー
  const handleDatasetChange = useCallback((newDatasetId: DatasetId) => {
    setSelectedDatasetId(newDatasetId);
    setSelectedColumns([]);
    setColumnActions({});
    setColumnScales({});

    // タブタイトルを更新
    const newDataset = datasets[newDatasetId];
    if (newDataset) {
      updateTabTitle(tab.id, `Dummy Coding: ${newDataset.name}`);
      logger.info('[DummyCodingTabContent] Dataset changed to:', newDataset.name);
    }

    // TabStateのmetadataを更新
    const result = findTab(tab.id);
    if (result) {
      updateTabState(result.container.id, tab.id, (currentTab) => ({
        ...currentTab,
        metadata: {
          ...currentTab.metadata,
          datasetId: newDatasetId
        }
      }));
    }
  }, [datasets, tab.id, updateTabTitle, findTab, updateTabState]);

  // 初回マウント時: datasetIdがあればタイトル更新と列の初期化
  React.useEffect(() => {
    if (selectedDatasetId && dataset) {
      updateTabTitle(tab.id, `Dummy Coding: ${dataset.name}`);

      // 全ての列をinclude_as_isで初期化
      const allColumnIds = dataset.schema.columns.map(col => col.id);
      const initialActions: ColumnActionMap = {};
      allColumnIds.forEach(colId => {
        initialActions[colId] = 'include_as_is';
      });

      setSelectedColumns(allColumnIds);
      setColumnActions(initialActions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDatasetId]);

  // Cancelハンドラー
  const handleCancel = useCallback(() => {
    const containerId = tabOperations.getActiveContainerId();
    if (containerId) {
      paneLayout.closeTab(containerId, tab.id);
    }
  }, [tab.id, tabOperations, paneLayout]);

  // Apply準備ハンドラー
  const handlePrepareApply = useCallback(() => {
    if (!dataset) return;

    setNewDatasetName(`${dataset.name} (Dummy Coded)`);
    setShowNameDialog(true);
  }, [dataset]);

  // 実際のApply処理
  const handleConfirmApply = useCallback(async () => {
    if (!dataset || !selectedDatasetId || !newDatasetName.trim()) {
      logger.warn('[DummyCodingTabContent] Cannot apply: missing required data');
      return;
    }

    setShowNameDialog(false);
    setIsApplying(true);

    try {
      logger.info('[DummyCodingTabContent] Starting dummy coding', {
        selectedColumns: selectedColumns.length
      });

      // columnsToEncode: dummy_code または dummy_code_with_original アクションが設定された列のセット
      const columnsToEncode = new Set(
        selectedColumns.filter(colId => {
          const action = columnActions[colId];
          return action === 'dummy_code' || action === 'dummy_code_with_original';
        })
      );

      // columnsToKeepOriginal: dummy_code_with_original アクションが設定された列のセット（元の列も残す）
      const columnsToKeepOriginal = new Set(
        selectedColumns.filter(colId => columnActions[colId] === 'dummy_code_with_original')
      );

      // schemaを取得するために一度データを生成（Issue #172: dataは保存しない）
      const result = createDummyCodedDataset(dataset, selectedColumns, columnsToEncode, columnScales, columnsToKeepOriginal);

      const timestamp = Date.now();
      const derivedDataset: DerivedDataSet = {
        id: createDatasetId(`dummy_coded_${timestamp}`),
        name: newDatasetName.trim(),
        type: 'derived' as const,
        schema: result.dataset.schema,
        // dataは含めない（executeOperationで再生成される）
        parentDatasetIds: [selectedDatasetId],
        operation: {
          id: createTransformOperationId(`dummy_coding_${timestamp}`),
          type: 'dummy_coding' as const,
          targetDatasetId: selectedDatasetId,
          encodings: result.categoricalEncodings
        },
        metadata: {
          createdAt: new Date().toISOString(),
          basedOn: [selectedDatasetId],
          description: `Dummy-coded dataset with ${result.categoricalEncodings.length} categorical variable(s) encoded`
        }
      };

      addDerivedDataset(derivedDataset);

      const activeContainerId = tabOperations.getActiveContainerId();
      if (activeContainerId) {
        const newTab = createDataTableTab(derivedDataset.id, derivedDataset.name);
        paneLayout.addTab(activeContainerId, newTab);
      }

      handleCancel();

      logger.info('[DummyCodingTabContent] Successfully created dummy-coded dataset:', derivedDataset.id);
    } catch (error) {
      logger.error('[DummyCodingTabContent] Failed to apply dummy coding:', error);
      showError(`Failed to apply dummy coding: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsApplying(false);
    }
  }, [dataset, selectedDatasetId, newDatasetName, selectedColumns, columnActions, columnScales, addDerivedDataset, tabOperations, paneLayout, handleCancel, showError]);

  // データセット選択ドロップダウン
  const datasetSelector = (
    <div className={styles.datasetSelector}>
      <label htmlFor="dataset-select" className={styles.datasetSelectorLabel}>Dataset:</label>
      <select
        id="dataset-select"
        className={styles.datasetSelectorSelect}
        value={selectedDatasetId ?? ''}
        onChange={(e) => handleDatasetChange(e.target.value as DatasetId)}
        disabled={Object.keys(datasets).length === 0}
      >
        <option value="" disabled>
          {Object.keys(datasets).length === 0
            ? 'No datasets available'
            : 'Select a dataset...'}
        </option>
        {Object.values(datasets).map(ds => (
          <option key={ds.id} value={ds.id}>
            {ds.name} ({ds.type})
          </option>
        ))}
      </select>
    </div>
  );

  // データセット未選択時
  if (!dataset) {
    return (
      <div className={styles.fullContainer}>
        {datasetSelector}
        <div className={styles.placeholderContainer}>
          <p>Select a dataset to create dummy variables from categorical columns.</p>
        </div>
      </div>
    );
  }

  // 何らかのアクションが設定されているかチェック
  const hasAnyAction = selectedColumns.length > 0;

  const canApply = hasAnyAction;

  return (
    <div className={styles.fullContainer}>
      {datasetSelector}
      <div className={styles.contentContainer}>
        <DummyCodingSettings
          dataset={dataset}
          selectedColumns={selectedColumns}
          onSelectedColumnsChange={setSelectedColumns}
          columnActions={columnActions}
          onColumnActionsChange={setColumnActions}
          columnScales={columnScales}
          onColumnScalesChange={setColumnScales}
        />
        <div className={styles.buttonContainer}>
          <div className={styles.spacer} />
          <button className={styles.secondaryButton} onClick={handleCancel}>
            Cancel
          </button>
          <button
            className={styles.primaryButton}
            onClick={handlePrepareApply}
            disabled={!canApply || isApplying}
          >
            {isApplying ? 'Creating...' : 'Create Dataset'}
          </button>
        </div>
      </div>

      {showNameDialog && (
        <Dialog
          isOpen={showNameDialog}
          onClose={() => setShowNameDialog(false)}
          title="Save Dummy-Coded Dataset"
          footer={
            <>
              <Button variant="secondary" onClick={() => setShowNameDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmApply}
                disabled={!newDatasetName.trim()}
              >
                OK
              </Button>
            </>
          }
        >
          <div style={{ padding: '16px' }}>
            <label htmlFor="dataset-name" style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              Dataset Name:
            </label>
            <input
              id="dataset-name"
              type="text"
              value={newDatasetName}
              onChange={(e) => setNewDatasetName(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #d0d0d0',
                borderRadius: '4px',
                boxSizing: 'border-box',
              }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newDatasetName.trim()) {
                  handleConfirmApply();
                }
              }}
            />
          </div>
        </Dialog>
      )}
    </div>
  );
};
